import { useMutation, useQueryClient } from '@tanstack/react-query';
import { closeConversation, isApiError, reopenConversation } from '../../api';
import { invalidateConversationLists, syncConversation } from './cache';
import { conversationKey } from './keys';

/** Mensagem para a pessoa quando encerrar ou reabrir falha (o texto cru do servidor nem sempre serve). */
export function statusErrorMessage(error: unknown, to: 'open' | 'closed'): string {
  if (isApiError(error)) {
    // O cliente de API já troca o texto cru por uma frase em português (outra aba mudou a sessão).
    if (error.code === 'invalid_csrf') return error.message;
    if (error.code === 'not_found') return 'Esta conversa não existe mais ou você não pode vê-la.';
    if (error.status === 403) return 'Só a pessoa responsável ou um administrador pode fazer isso.';
    if (error.status >= 500)
      return 'O servidor está indisponível no momento. Tente de novo em instantes.';
    return error.message;
  }
  return to === 'closed'
    ? 'Não foi possível encerrar a conversa. Tente de novo.'
    : 'Não foi possível reabrir a conversa. Tente de novo.';
}

/**
 * Encerrar ou reabrir uma conversa. Ao terminar, a conversa aberta e as listas mudam: ela sai de
 * "Minhas" (por exemplo) e passa a estar em "Encerradas". Em caso de erro a conversa é buscada de
 * novo: quem agiu pode ter perdido a conversa (outra pessoa assumiu) e a tela mostra o que é verdade.
 */
export function useConversationStatus(conversationId: string) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (to: 'open' | 'closed') =>
      to === 'closed' ? closeConversation(conversationId) : reopenConversation(conversationId),
    onSuccess: (conversation) => {
      syncConversation(queryClient, conversation);
      void invalidateConversationLists(queryClient);
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
      void invalidateConversationLists(queryClient);
    },
  });
  return {
    change: (to: 'open' | 'closed', callbacks?: { onSuccess?: () => void }) =>
      mutation.mutate(to, callbacks),
    pending: mutation.isPending,
    error: mutation.isError
      ? statusErrorMessage(mutation.error, mutation.variables ?? 'closed')
      : null,
    reset: mutation.reset,
  };
}
